<template>
  <div class="contracts-modern">
    <NavBar />
    <AuthBySessionExpired />
    <new-form />

    <div class="contracts-container">
      <!-- Header Section -->
      <div class="contracts-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-return" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-file-contract title-icon"></i>
              Contratos
            </h1>
            <p class="page-subtitle">
              Gerencie os modelos de contratos da instituição
            </p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="permission('write', 'contract')"
              class="btn-add"
              @click="openNewContract"
            >
              <i class="fas fa-plus mr-2"></i>
              Novo Contrato
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-grid">
          <div class="filter-group filter-group--search">
            <label class="filter-label">
              <i class="fas fa-search filter-icon"></i>
              Pesquisar
            </label>
            <v-text-field
              dense
              placeholder="Nome do contrato..."
              outlined
              hide-details
              v-model="search"
              class="modern-input"
            />
          </div>

          <div class="filter-group filter-group--info">
            <div class="stats-badge">
              <i class="fas fa-file-alt mr-2"></i>
              <span class="stats-number">{{ contracts.length }}</span>
              <span class="stats-label"
                >contrato{{ contracts.length !== 1 ? "s" : "" }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="contracts-content">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <!-- Empty State -->
          <div v-if="!loading && contracts.length === 0" class="empty-state">
            <div class="empty-icon-container">
              <i class="fas fa-file-contract empty-icon"></i>
            </div>
            <h3 class="empty-title">Nenhum contrato cadastrado</h3>
            <p class="empty-text">
              Crie seu primeiro modelo de contrato para começar
            </p>
            <v-btn
              v-if="permission('write', 'contract')"
              class="btn-add mt-4"
              @click="openNewContract"
            >
              <i class="fas fa-plus mr-2"></i>
              Criar Contrato
            </v-btn>
          </div>

          <!-- Contracts Table -->
          <div v-else class="table-card">
            <v-data-table
              dense
              :search="search"
              :headers="tableHeaders"
              :items="contracts"
              must-sort
              sort-by="name"
              :sort-desc="false"
              :footer-props="{
                'items-per-page-options': [25, 50, 100],
                'items-per-page-text': 'Por página:',
              }"
              class="contracts-table"
            >
              <!-- Coluna: Nome -->
              <template v-slot:[`item.name`]="{ item }">
                <div class="contract-name">
                  <div class="contract-icon-wrapper">
                    <i class="fas fa-file-alt"></i>
                  </div>
                  <span>{{ item.name }}</span>
                </div>
              </template>

              <!-- Coluna: Destinatários -->
              <template v-slot:[`item.all_residents`]="{ item }">
                <div
                  v-if="item.all_residents == 1"
                  class="recipients-badge recipients-badge--all"
                >
                  <i class="fas fa-users mr-1"></i>
                  Todos os residentes
                </div>
                <div v-else class="recipients-list">
                  <div
                    v-if="item.residents && item.residents.length > 0"
                    class="recipients-summary"
                  >
                    <span class="recipients-count">
                      <i class="fas fa-user mr-1"></i>
                      {{ item.residents.length }} residente{{
                        item.residents.length !== 1 ? "s" : ""
                      }}
                    </span>
                    <button
                      type="button"
                      class="recipients-toggle"
                      @click="toggleResidents(item.id)"
                    >
                      <i
                        :class="
                          expandedResidents.includes(item.id)
                            ? 'fas fa-chevron-up'
                            : 'fas fa-chevron-down'
                        "
                      ></i>
                    </button>
                  </div>
                  <div
                    v-if="expandedResidents.includes(item.id) && item.residents"
                    class="recipients-expanded"
                  >
                    <span
                      v-for="resident in item.residents"
                      :key="`${item.id}-${resident.id}`"
                      class="recipient-tag"
                    >
                      {{ resident.name }}
                    </span>
                  </div>
                  <span
                    v-if="!item.residents || item.residents.length === 0"
                    class="no-residents"
                  >
                    Nenhum residente
                  </span>
                </div>
              </template>

              <!-- Coluna: Status -->
              <template v-slot:[`item.active`]="{ item }">
                <span
                  class="status-badge"
                  :class="
                    item.active == 1
                      ? 'status-badge--active'
                      : 'status-badge--inactive'
                  "
                >
                  <i
                    :class="
                      item.active == 1
                        ? 'fas fa-check-circle'
                        : 'fas fa-times-circle'
                    "
                    class="mr-1"
                  ></i>
                  {{ item.active == 1 ? "Ativo" : "Inativo" }}
                </span>
              </template>

              <!-- Coluna: Modificado -->
              <template v-slot:[`item.updated_at`]="{ item }">
                <span class="date-text">
                  <i class="fas fa-clock mr-1"></i>
                  {{ formatDate(item.updated_at) }}
                </span>
              </template>

              <!-- Coluna: Criado -->
              <template v-slot:[`item.created_at`]="{ item }">
                <span class="date-text">
                  <i class="fas fa-calendar mr-1"></i>
                  {{ formatDate(item.created_at) }}
                </span>
              </template>

              <!-- Coluna: Ações -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="permission('edit', 'contract')"
                  small
                  class="btn-edit"
                  @click="openContract(item)"
                >
                  <i class="fas fa-pencil-alt mr-1"></i>
                  Editar
                </v-btn>
              </template>
            </v-data-table>
          </div>

          <errors-messages :errors="errors" />
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import NewForm from "../templates/Semiology/NewForm.vue";
import ErrorsMessages from "../templates/Components/ErrorsMessages.vue";

import api from "../services/api";
import { formatDate } from "../lib/format";
import permission from "../lib/permission";
import routes from "../routes";

export default {
  name: "ContractStandards",

  components: {
    NavBar,
    AuthBySessionExpired,
    NewForm,
    ErrorsMessages,
  },

  data() {
    return {
      errors: [],
      loading: false,
      search: "",
      contracts: [],
      expandedResidents: [],

      tableHeaders: [
        { text: "Nome do Contrato", value: "name", width: "25%" },
        {
          text: "Destinatários",
          value: "all_residents",
          width: "25%",
          sortable: false,
        },
        { text: "Status", value: "active", width: "10%", align: "center" },
        { text: "Modificado", value: "updated_at", width: "14%" },
        { text: "Criado", value: "created_at", width: "14%" },
        {
          text: "Ações",
          value: "actions",
          width: "12%",
          sortable: false,
          align: "center",
        },
      ],
    };
  },

  created() {
    this.paginate();
  },

  methods: {
    permission,
    formatDate,
    handleToBack: () => routes.go(-1),

    async paginate() {
      try {
        this.loading = true;
        const response = await api.get("/contract/standards");
        const { error, errors, contracts } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.contracts = contracts || [];
        }
      } catch (error) {
        console.error("Erro ao carregar contratos:", error);
        this.errors = ["Erro ao carregar lista de contratos"];
      } finally {
        this.loading = false;
      }
    },

    toggleResidents(contractId) {
      const index = this.expandedResidents.indexOf(contractId);
      if (index > -1) {
        this.expandedResidents.splice(index, 1);
      } else {
        this.expandedResidents.push(contractId);
      }
    },

    openNewContract() {
      this.$router.push({ name: "contract_standard_new" });
    },

    openContract(item) {
      this.$router.push({ name: "contract_standard", params: { id: item.id } });
    },
  },
};
</script>

<style scoped>
/* ===========================================
   LAYOUT PRINCIPAL
   Seguindo padrão do Dashboard e Account
   =========================================== */

.contracts-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.contracts-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===========================================
   HEADER SECTION
   =========================================== */

.contracts-header {
  padding: 24px 0;
  margin-bottom: 24px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* ===========================================
   FILTERS SECTION
   =========================================== */

.filters-section {
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: flex;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group--search {
  flex: 1;
  min-width: 280px;
  max-width: 400px;
}

.filter-group--info {
  margin-left: auto;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-icon {
  color: #0f65b0;
}

.stats-badge {
  display: inline-flex;
  align-items: center;
  padding: 12px 20px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border-radius: 12px;
  font-weight: 600;
  border: 1px solid #93c5fd;
}

.stats-number {
  font-size: 20px;
  margin-right: 6px;
}

.stats-label {
  font-size: 14px;
}

/* ===========================================
   CONTENT SECTION
   =========================================== */

.contracts-content {
  margin-bottom: 32px;
}

/* ===========================================
   EMPTY STATE
   =========================================== */

.empty-state {
  background: white;
  border-radius: 16px;
  padding: 64px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.empty-icon-container {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.empty-icon {
  font-size: 48px;
  color: #94a3b8;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.empty-text {
  font-size: 16px;
  color: #64748b;
  margin: 0;
}

/* ===========================================
   TABLE CARD
   =========================================== */

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

/* Contract Name */
.contract-name {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #1e293b;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.contract-icon-wrapper {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f65b0;
  font-size: 14px;
}

/* Recipients Badge */
.recipients-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.recipients-badge--all {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

/* Recipients List */
.recipients-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recipients-summary {
  display: flex;
  align-items: center;
  gap: 8px;
}

.recipients-count {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

.recipients-toggle {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  color: #64748b;
  font-size: 10px;
  transition: all 0.2s ease;
}

.recipients-toggle:hover {
  background: #e2e8f0;
  color: #0f65b0;
}

.recipients-expanded {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px dashed #e2e8f0;
}

.recipient-tag {
  display: inline-block;
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
  color: #475569;
}

.no-residents {
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge--active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-badge--inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

/* Date Text */
.date-text {
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
}

.date-text i {
  font-size: 11px;
  color: #94a3b8;
}

/* ===========================================
   RESPONSIVE
   =========================================== */

@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .actions-section {
    justify-self: start;
  }
}

@media (max-width: 768px) {
  .contracts-container {
    padding: 0 16px;
  }

  .contracts-header {
    padding: 16px 0;
  }

  .page-title {
    font-size: 24px;
  }

  .filters-grid {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group--search {
    max-width: 100%;
  }

  .filter-group--info {
    margin-left: 0;
  }

  .empty-state {
    padding: 48px 16px;
  }

  .empty-icon-container {
    width: 80px;
    height: 80px;
  }

  .empty-icon {
    font-size: 36px;
  }

  .empty-title {
    font-size: 20px;
  }
}

/* ===========================================
   VUETIFY OVERRIDES
   =========================================== */

.modern-input >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-input >>> .v-text-field__slot input {
  font-weight: 500 !important;
}
</style>